import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import { api, type User } from '../api/client';
import { T } from '../styles/tokens';
import { haptic, tg } from '../telegram/sdk';

interface Props {
  onOpenReferral: () => void;
  onOpenPayments: () => void;
}

const SUPPORT_URL = 'https://t.me/xelllinson';
const BOT_URL = 'https://t.me/vpnsalerpy_bot';

export default function More({ onOpenReferral, onOpenPayments }: Props) {
  const [me, setMe] = useState<User | null>(null);

  useEffect(() => {
    api.me.get().then(setMe).catch(() => {});
  }, []);

  const items: Array<{
    label: string;
    desc: string;
    onClick: () => void;
    tag: string;
  }> = [
    {
      label: 'Реферальная программа',
      desc: 'Пригласи друга — +7 дней',
      onClick: () => {
        haptic('light');
        onOpenReferral();
      },
      tag: 'referral',
    },
    {
      label: 'История платежей',
      desc: 'Ваши покупки',
      onClick: () => {
        haptic('light');
        onOpenPayments();
      },
      tag: 'payments',
    },
    {
      label: 'Поддержка',
      desc: '@xelllinson',
      onClick: () => {
        haptic('light');
        tg()?.openTelegramLink ? tg()!.openTelegramLink!(SUPPORT_URL) : tg()?.openLink(SUPPORT_URL);
      },
      tag: 'support',
    },
    {
      label: 'Telegram-бот',
      desc: '@vpnsalerpy_bot',
      onClick: () => {
        haptic('light');
        tg()?.openTelegramLink ? tg()!.openTelegramLink!(BOT_URL) : tg()?.openLink(BOT_URL);
      },
      tag: 'bot',
    },
  ];

  const initials = initialsFrom(me?.name, me?.username);
  const displayName = me?.name || me?.username || 'Гость';
  const subtitle = me
    ? [me.username ? `@${me.username}` : null, me.id ? `ID ${me.id}` : null].filter(Boolean).join(' · ')
    : '—';

  return (
    <div style={m.wrap}>
      <div style={m.header}>
        <div style={m.headTag}>// меню</div>
        <div style={m.headTitle}>Ещё</div>
      </div>

      <div style={m.userCard}>
        <div style={m.ava}>{initials}</div>
        <div style={{ minWidth: 0 }}>
          <div style={m.userName}>{displayName}</div>
          <div style={m.userId}>{subtitle}</div>
        </div>
      </div>

      <div style={m.list}>
        {items.map((it) => (
          <button key={it.tag} style={m.item} onClick={it.onClick}>
            <div style={m.itemLeft}>
              <div style={m.itemLabel}>{it.label}</div>
              <div style={m.itemDesc}>{it.desc}</div>
            </div>
            <span style={m.arrow}>→</span>
          </button>
        ))}
      </div>

      <div style={m.footer}>VPN Saler · v0.2.0</div>
    </div>
  );
}

function initialsFrom(name?: string | null, username?: string | null): string {
  const source = (name || username || '?').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const m: Record<string, CSSProperties> = {
  wrap: { padding: '20px 16px 24px' },
  header: { marginBottom: 18 },
  headTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 },
  headTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 24, letterSpacing: '-0.03em' },
  userCard: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 },
  ava: { width: 42, height: 42, background: T.accentSoft, color: T.accent, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'DM Mono, monospace', fontWeight: 700, fontSize: 14, flexShrink: 0 },
  userName: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' as const, overflow: 'hidden' as const, textOverflow: 'ellipsis' as const },
  userId: { fontFamily: 'DM Mono, monospace', fontSize: 11, color: T.textMuted, marginTop: 2, whiteSpace: 'nowrap' as const, overflow: 'hidden' as const, textOverflow: 'ellipsis' as const },
  list: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 },
  item: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', color: T.text, textAlign: 'left' },
  itemLeft: {},
  itemLabel: { fontSize: 14, fontWeight: 600, marginBottom: 2 },
  itemDesc: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, letterSpacing: '0.04em' },
  arrow: { color: T.accent, fontFamily: 'DM Mono, monospace', fontSize: 14 },
  footer: { textAlign: 'center', fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, letterSpacing: '0.04em', marginTop: 28 },
};
